import { GET_RENDER_FOLDER_OPTIONS } from '@/graphql/projects';
import { DAWType } from '../../types/daw';
import {
  isDAWFile,
  getDAWTypeFromExtension,
  filterTopLevelProjectFolders,
} from './dawUtils';

const RENDER_FOLDERS = Object.keys(GET_RENDER_FOLDER_OPTIONS).map((k) =>
  k.toLowerCase()
);

interface FileWithRelativePath extends File {
  webkitRelativePath: string;
}

export interface DetectedProject {
  folderPath: string;
  projectName: string;
  dawType: DAWType;
  fileCount: number;
  totalSize: number;
  dawFiles: string[];
  rendersFolder: string | null;
  earliestModified?: number;
  latestModified?: number;
}

export const ROOT_PROJECT_FOLDER_PATH = '.';

/** Audio file extensions accepted for stems uploads. */
export const STEMS_AUDIO_EXTENSIONS = new Set([
  '.wav',
  '.wave',
  '.mp3',
  '.aiff',
  '.aif',
  '.aifc',
  '.flac',
  '.ogg',
  '.oga',
  '.m4a',
  '.aac',
  '.opus',
  '.wma',
  '.mid',
  '.midi',
]);

/**
 * Build a single DetectedProject from a flat collection of audio files.
 * Called after filtering to STEMS_AUDIO_EXTENSIONS.
 */
export function detectStemsProject(
  folderName: string,
  files: File[]
): DetectedProject {
  let earliestModified: number | undefined;
  let latestModified: number | undefined;
  let totalSize = 0;

  files.forEach((f) => {
    totalSize += f.size;
    if (f.lastModified) {
      if (earliestModified === undefined || f.lastModified < earliestModified) {
        earliestModified = f.lastModified;
      }
      if (latestModified === undefined || f.lastModified > latestModified) {
        latestModified = f.lastModified;
      }
    }
  });

  return {
    folderPath: ROOT_PROJECT_FOLDER_PATH,
    projectName: folderName,
    dawType: DAWType.STEMS,
    fileCount: files.length,
    totalSize,
    dawFiles: [],
    rendersFolder: null,
    earliestModified,
    latestModified,
  };
}

interface DetectProjectsOptions {
  rootFolderName?: string;
}

export interface DetectedProjectFile {
  name: string;
  relativePath: string;
  size: number;
  type: string;
  isDAWFile: boolean;
  dawType?: string;
}

/**
 * Detect DAW projects from a FileList (from folder upload)
 * Scans recursively at any depth - any folder containing DAW files is considered a project
 */
export function detectProjectsFromFiles(
  files: FileList,
  options: DetectProjectsOptions = {}
): DetectedProject[] {
  const projectMap = new Map<string, DetectedProject>();
  // Track which folders contain DAW files directly (at any depth)
  const foldersWithDAWFiles = new Set<string>();
  const rootFolderName = options.rootFolderName || 'Selected Folder';
  let hasRootLevelDAWFiles = false;

  // First pass: identify all folders that contain DAW files directly
  Array.from(files).forEach((file) => {
    const fileWithPath = file as FileWithRelativePath;
    const relativePath = fileWithPath.webkitRelativePath || file.name;
    const pathParts = relativePath.split('/');
    const fileName = pathParts[pathParts.length - 1];

    // Track root-level DAW files so a selected single project folder is supported.
    if (pathParts.length < 2) {
      if (isDAWFile(fileName)) {
        hasRootLevelDAWFiles = true;
      }
      return;
    }

    // If this is a DAW file, mark its direct parent folder as containing DAW files
    if (isDAWFile(fileName)) {
      const directParentPath = pathParts.slice(0, -1).join('/');
      foldersWithDAWFiles.add(directParentPath);
    }
  });

  // Filter to top-level project folders (exclude subfolders of projects).
  // If the selected root already contains DAW files, treat that root as the
  // single project and do not promote child folders (e.g. Backup) to projects.
  const topLevelProjects = hasRootLevelDAWFiles
    ? new Set<string>()
    : filterTopLevelProjectFolders(foldersWithDAWFiles);

  if (hasRootLevelDAWFiles) {
    projectMap.set(ROOT_PROJECT_FOLDER_PATH, {
      folderPath: ROOT_PROJECT_FOLDER_PATH,
      projectName: rootFolderName,
      dawType: DAWType.UNKNOWN,
      fileCount: 0,
      totalSize: 0,
      dawFiles: [],
      rendersFolder: null,
      earliestModified: undefined,
      latestModified: undefined,
    });
  }

  // Second pass: create projects using only top-level project folders
  Array.from(files).forEach((file) => {
    const fileWithPath = file as FileWithRelativePath;
    const relativePath = fileWithPath.webkitRelativePath || file.name;
    const pathParts = relativePath.split('/');
    const fileName = pathParts[pathParts.length - 1];

    // Include all files in the root project when a DAW file exists at root.
    if (hasRootLevelDAWFiles) {
      const rootProject = projectMap.get(ROOT_PROJECT_FOLDER_PATH);
      if (!rootProject) {
        return;
      }

      rootProject.fileCount++;
      rootProject.totalSize += file.size;

      const fileModified = file.lastModified;
      if (
        rootProject.earliestModified === undefined ||
        fileModified < rootProject.earliestModified
      ) {
        rootProject.earliestModified = fileModified;
      }
      if (
        rootProject.latestModified === undefined ||
        fileModified > rootProject.latestModified
      ) {
        rootProject.latestModified = fileModified;
      }

      if (isDAWFile(fileName)) {
        rootProject.dawFiles.push(fileName);
        const extension = fileName.substring(fileName.lastIndexOf('.'));
        const dawType = getDAWTypeFromExtension(extension);
        if (rootProject.dawType === DAWType.UNKNOWN) {
          rootProject.dawType = dawType;
        }
      }

      if (!rootProject.rendersFolder && pathParts.length >= 2) {
        const firstSegment = pathParts[0];
        if (RENDER_FOLDERS.includes(firstSegment.toLowerCase())) {
          rootProject.rendersFolder = firstSegment;
        }
      }

      return;
    }

    // Find the deepest folder in this file's path that is a top-level project
    // This ensures subfolders (e.g., backups) are not treated as separate projects
    let projectFolderPath: string | null = null;
    for (let i = pathParts.length - 1; i >= 1; i--) {
      const folderPath = pathParts.slice(0, i).join('/');
      if (topLevelProjects.has(folderPath)) {
        projectFolderPath = folderPath;
        break;
      }
    }

    // If no project folder found, skip this file
    if (!projectFolderPath) {
      return;
    }

    // Initialize project if not exists
    if (!projectMap.has(projectFolderPath)) {
      const pathPartsForProject = projectFolderPath.split('/');
      const projectName = pathPartsForProject[pathPartsForProject.length - 1];

      projectMap.set(projectFolderPath, {
        folderPath: projectFolderPath,
        projectName,
        dawType: DAWType.UNKNOWN,
        fileCount: 0,
        totalSize: 0,
        dawFiles: [],
        rendersFolder: null,
        earliestModified: undefined,
        latestModified: undefined,
      });
    }

    const project = projectMap.get(projectFolderPath)!;
    project.fileCount++;
    project.totalSize += file.size;

    // Detect renders folder
    if (!project.rendersFolder) {
      const relPath = relativePath.slice(projectFolderPath.length + 1);
      const firstSegment = relPath.split('/')[0];
      if (
        firstSegment &&
        relPath.includes('/') &&
        RENDER_FOLDERS.includes(firstSegment.toLowerCase())
      ) {
        project.rendersFolder = firstSegment;
      }
    }

    // Track file modification times
    const fileModified = file.lastModified;
    if (
      project.earliestModified === undefined ||
      fileModified < project.earliestModified
    ) {
      project.earliestModified = fileModified;
    }
    if (
      project.latestModified === undefined ||
      fileModified > project.latestModified
    ) {
      project.latestModified = fileModified;
    }

    // Check if this is a DAW file
    if (isDAWFile(fileName)) {
      project.dawFiles.push(fileName);
      const extension = fileName.substring(fileName.lastIndexOf('.'));
      const dawType = getDAWTypeFromExtension(extension);

      // Set DAW type (prioritize the first DAW file found)
      if (project.dawType === DAWType.UNKNOWN) {
        project.dawType = dawType;
      }
    }
  });

  // Return all detected projects (already filtered to only folders with DAW files)
  return Array.from(projectMap.values());
}

/**
 * Get files for a specific detected project
 */
export function getProjectFiles(
  files: FileList,
  projectFolderPath: string
): File[] {
  if (projectFolderPath === ROOT_PROJECT_FOLDER_PATH) {
    return Array.from(files);
  }

  return Array.from(files).filter((file) => {
    const fileWithPath = file as FileWithRelativePath;
    const relativePath = fileWithPath.webkitRelativePath || file.name;
    // Check if the file path starts with the project path
    // Handle both single-level and multi-level project paths
    return (
      relativePath.startsWith(projectFolderPath + '/') ||
      relativePath === projectFolderPath
    );
  });
}

/**
 * Generate project name suggestions
 */
export const suggestProjectName = (folderName: string): string => {
  // Clean up folder name for project name
  return folderName
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get project statistics summary
 */
export const getProjectStats = (project: DetectedProject): string => {
  const stats: string[] = [];

  stats.push(`${project.fileCount} files`);

  if (project.dawFiles.length > 0) {
    stats.push(`${project.dawFiles.length} ${project.dawType} files`);
  }

  return stats.join(' • ');
};
